import React, { useState, useEffect, useCallback } from 'react';
import donneesApi from '../services/donnees';
import './ExplorateurFg.css';

// ─── Explorateur du Grand livre des frais généraux ───────────────────────────
// Navigation par FOURNISSEUR : liste triée par montant (filtres société /
// année / recherche), puis fiche fournisseur — ventilation par nature, par
// compte, série mensuelle et dernières écritures. Lecture pure de
// marts.grand_livre_fg via le backend (rôle finance_readonly).

const eur = (v) => (v == null ? '—' : Number(v).toLocaleString('fr-FR', { maximumFractionDigits: 0 }) + ' €');

function MiniBarres({ mensuel }) {
  if (!mensuel || mensuel.length < 2) return null;
  const vals = mensuel.map(m => Math.abs(m.total_ht || 0));
  const max = Math.max(...vals, 1);
  return (
    <div className="xfg-barres" title="Montant HT par mois">
      {mensuel.map((m, i) => (
        <div key={i} className="xfg-barre-wrap" title={`${String(m.mois).padStart(2, '0')}/${m.annee} : ${eur(m.total_ht)}`}>
          <div className={`xfg-barre${(m.total_ht || 0) < 0 ? ' xfg-barre-neg' : ''}`}
               style={{ height: `${Math.max(2, (Math.abs(m.total_ht || 0) / max) * 46)}px` }} />
        </div>
      ))}
    </div>
  );
}

export default function ExplorateurFg({ onClose }) {
  const [dims, setDims] = useState({ societes: [], annees: [] });
  const [societe, setSociete] = useState('');
  const [annee, setAnnee] = useState('');
  const [q, setQ] = useState('');
  const [fournisseurs, setFournisseurs] = useState(null);
  const [detail, setDetail] = useState(null);      // fiche fournisseur ouverte
  const [chargeDetail, setChargeDetail] = useState(false);

  useEffect(() => {
    donneesApi.fgDimensions().then(r => setDims(r.data)).catch(() => {});
  }, []);

  const charger = useCallback(() => {
    const params = {};
    if (societe) params.societe = societe;
    if (annee) params.annee = annee;
    if (q.trim().length >= 2) params.q = q.trim();
    donneesApi.fgFournisseurs(params)
      .then(r => setFournisseurs(r.data.fournisseurs || []))
      .catch(e => alert(e.response?.data?.error || e.message));
  }, [societe, annee, q]);

  useEffect(() => {
    const t = setTimeout(charger, 300);            // debounce recherche
    return () => clearTimeout(t);
  }, [charger]);

  const ouvrirFiche = async (code) => {
    setChargeDetail(true);
    try {
      const params = {};
      if (societe) params.societe = societe;
      if (annee) params.annee = annee;
      const r = await donneesApi.fgFournisseur(code, params);
      setDetail(r.data);
    } catch (e) {
      alert(e.response?.data?.error || e.message);
    } finally {
      setChargeDetail(false);
    }
  };

  const totalListe = (fournisseurs || []).reduce((s, f) => s + (f.total_ht || 0), 0);

  return (
    <div className="xfg-overlay" onClick={onClose}>
      <div className="xfg-fenetre" onClick={e => e.stopPropagation()}>
        <div className="xfg-tete">
          {detail ? (
            <button className="xfg-retour" onClick={() => setDetail(null)}>← Fournisseurs</button>
          ) : (
            <b>Grand livre — frais généraux par fournisseur</b>
          )}
          <span className="xfg-tete-info">
            {detail
              ? `${detail.fournisseur || detail.fournisseur_code} · ${detail.ecritures} écritures · ${eur(detail.total_ht)}`
              : fournisseurs ? `${fournisseurs.length} fournisseurs · ${eur(totalListe)}` : ''}
          </span>
          <button className="xfg-fermer" onClick={onClose}>✕</button>
        </div>

        {!detail && (
          <div className="xfg-toolbar">
            <input className="xfg-recherche" placeholder="🔍 Fournisseur…" value={q} onChange={e => setQ(e.target.value)} />
            <select value={societe} onChange={e => setSociete(e.target.value)}>
              <option value="">Toutes sociétés</option>
              {dims.societes.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
            <select value={annee} onChange={e => setAnnee(e.target.value)}>
              <option value="">Toutes années</option>
              {dims.annees.map(a => <option key={a} value={a}>{a}</option>)}
            </select>
          </div>
        )}

        <div className="xfg-corps">
          {!detail && !fournisseurs && <div className="xfg-vide">Chargement…</div>}

          {!detail && fournisseurs && (
            <table className="xfg-table">
              <thead>
                <tr>
                  <th>Fournisseur</th><th className="xfg-num">Écritures</th>
                  <th className="xfg-num">2024</th><th className="xfg-num">2025</th><th className="xfg-num">2026</th>
                  <th className="xfg-num">Total HT</th><th>Dernière pièce</th>
                </tr>
              </thead>
              <tbody>
                {fournisseurs.map(f => (
                  <tr key={f.fournisseur_code} onClick={() => ouvrirFiche(f.fournisseur_code)}
                      className={chargeDetail ? 'xfg-attente' : ''}>
                    <td><b>{f.fournisseur || f.fournisseur_code}</b><span className="xfg-code"> {f.fournisseur_code}</span></td>
                    <td className="xfg-num">{f.ecritures.toLocaleString('fr-FR')}</td>
                    <td className="xfg-num">{eur(f.ht_2024)}</td>
                    <td className="xfg-num">{eur(f.ht_2025)}</td>
                    <td className="xfg-num">{eur(f.ht_2026)}</td>
                    <td className="xfg-num"><b>{eur(f.total_ht)}</b></td>
                    <td>{f.derniere_piece ? f.derniere_piece.split('-').reverse().join('/') : '—'}</td>
                  </tr>
                ))}
                {fournisseurs.length === 0 && (
                  <tr><td colSpan={7} className="xfg-vide">Aucun fournisseur ne correspond.</td></tr>
                )}
              </tbody>
            </table>
          )}

          {detail && (
            <div className="xfg-fiche">
              <MiniBarres mensuel={detail.mensuel} />

              <div className="xfg-colonnes">
                <div>
                  <h4>Par nature de coût</h4>
                  <table className="xfg-table xfg-table-compacte">
                    <tbody>
                      {detail.par_nature.map((n, i) => (
                        <tr key={i}>
                          <td>{n.nature2 || n.nature1 || '—'}<span className="xfg-code"> {n.nature1 !== n.nature2 ? n.nature1 : ''}</span></td>
                          <td className="xfg-num">{n.ecritures}</td>
                          <td className="xfg-num"><b>{eur(n.total_ht)}</b></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div>
                  <h4>Par compte général</h4>
                  <table className="xfg-table xfg-table-compacte">
                    <tbody>
                      {detail.par_compte.map((c, i) => (
                        <tr key={i}>
                          <td>{c.libelle}<span className="xfg-code"> {c.compte} · {c.code_societe}</span></td>
                          <td className="xfg-num">{c.ecritures}</td>
                          <td className="xfg-num"><b>{eur(c.total_ht)}</b></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <h4>Dernières écritures{detail.ecritures > 300 ? ` (300 sur ${detail.ecritures.toLocaleString('fr-FR')})` : ` (${detail.ecritures})`}</h4>
              <div className="xfg-scroll">
                <table className="xfg-table xfg-table-compacte">
                  <thead>
                    <tr><th>Date</th><th>Société</th><th>Pièce</th><th>Libellé</th><th>Compte</th><th>Nature</th><th>Analytique</th><th className="xfg-num">Montant HT</th></tr>
                  </thead>
                  <tbody>
                    {(detail.lignes || []).map((e, i) => (
                      <tr key={i}>
                        <td>{e.date_piece ? e.date_piece.split('-').reverse().join('/') : '—'}</td>
                        <td>{e.code_societe}</td>
                        <td className="xfg-code">{e.num_piece}</td>
                        <td>{e.libelle_ecriture}</td>
                        <td title={e.compte}>{e.libelle_compte}</td>
                        <td>{e.nature2 || '—'}</td>
                        <td>{e.analytique || '—'}</td>
                        <td className="xfg-num"><b>{eur(e.montant_ht)}</b></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
